import { useState, useEffect } from 'react';
import { Link2, Plus, X, ExternalLink, RefreshCw, ToggleLeft, ToggleRight, AlertCircle, ArrowDownToLine, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import apiClient from '../../api/client';

/**
 * PartIHSLinkSection - Manage IHS carline links for a part
 * 
 * Features:
 * - View linked IHS carlines with volume data
 * - Link/unlink carlines
 * - Toggle auto-sync
 * - Show aggregated base volume
 * - Auto-sync IHS volume to S&OP grid on link/unlink
 */
export default function PartIHSLinkSection({ partId, partNumber, programId, programContext, onVolumeSync }) {
  const [linkedCarlines, setLinkedCarlines] = useState([]);
  const [availableCarlines, setAvailableCarlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    if (partId) {
      fetchLinkedCarlines();
    }
  }, [partId, programId]);

  const fetchLinkedCarlines = async () => {
    try {
      setLoading(true);
      // URL encode the part number to handle spaces and special characters
      const encodedPartNumber = encodeURIComponent(partNumber);
      const response = await apiClient.get(`/api/v1/parts/${encodedPartNumber}/carline-links`, {
        params: programId ? { program_id: programId } : {}
      });
      if (response.data.success) {
        setLinkedCarlines(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch linked carlines:', error);
      // API might not exist yet, that's ok
      setLinkedCarlines([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCarlines = async (search = '') => {
    try {
      const response = await apiClient.get('/api/v1/ihs/carlines', {
        params: { search, limit: 50 }
      });
      if (response.data.success) {
        setAvailableCarlines(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch available carlines:', error);
      setAvailableCarlines([]);
    }
  };

  const handleOpenLinkDialog = () => {
    fetchAvailableCarlines('');
    setShowLinkDialog(true);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchAvailableCarlines(term);
  };

  const handleLinkCarline = async (carline) => {
    try {
      setLinking(true);
      const encodedPartNumber = encodeURIComponent(partNumber);
      const response = await apiClient.post(`/api/v1/parts/${encodedPartNumber}/carline-links`, {
        core_mnemonic: carline.core_mnemonic,
        ...(programId ? { program_id: programId } : {})
      }, {
        params: programId ? { program_id: programId } : {}
      });
      await fetchLinkedCarlines();
      
      // Show toast with result message
      const data = response.data?.data;
      const message = response.data?.message || "Carline linked successfully";
      
      toast({
        title: "Carline Linked",
        description: message,
        duration: 5000,
      });
      
      // Notify parent that IHS volume was synced (triggers page refresh if dates were auto-filled)
      if (data?.ihs_synced && onVolumeSync) {
        onVolumeSync();
      }
      
      // If dates were auto-filled, also notify about that
      if (data?.dates_auto_filled) {
        // Dispatch a custom event to notify the parent to refresh part data
        window.dispatchEvent(new CustomEvent('partDatesUpdated', { 
          detail: { partNumber, sop: data.sop_date, eop: data.eop_date } 
        }));
      }
    } catch (error) {
      console.error('Failed to link carline:', error);
      toast({
        title: "Failed to Link Carline",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkCarline = async (linkId) => {
    try {
      const encodedPartNumber = encodeURIComponent(partNumber);
      await apiClient.delete(`/api/v1/parts/${encodedPartNumber}/carline-links/${linkId}`, {
        params: programId ? { program_id: programId } : {}
      });
      await fetchLinkedCarlines();
      
      // Notify parent that IHS volume was synced
      if (onVolumeSync) {
        onVolumeSync();
      }
    } catch (error) {
      console.error('Failed to unlink carline:', error);
    }
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const encodedPartNumber = encodeURIComponent(partNumber);
      const response = await apiClient.post(`/api/v1/parts/${encodedPartNumber}/sync-ihs-volume`, null, {
        params: programId ? { program_id: programId } : {}
      });
      
      if (response.data.success) {
        // Store sync result for display
        setSyncResult({
          success: true,
          message: response.data.message,
          details: response.data.details
        });
        
        // Show success toast
        toast({
          title: "Volume Synced Successfully",
          description: response.data.message,
          duration: 5000,
        });
        
        // Notify parent that IHS volume was synced
        if (onVolumeSync) {
          onVolumeSync();
        }
      } else {
        setSyncResult({
          success: false,
          error: response.data.error
        });
        
        // Show error toast
        toast({
          title: "Sync Failed",
          description: response.data.error || "Failed to sync IHS volume",
          variant: "destructive",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Failed to sync IHS volume:', error);
      const errorMsg = error.response?.data?.error || error.message || "An unexpected error occurred";
      
      setSyncResult({
        success: false,
        error: errorMsg
      });
      
      toast({
        title: "Sync Failed",
        description: errorMsg,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setSyncing(false);
    }
  };

  // Calculate total volume from linked carlines
  const totalBaseVolume = linkedCarlines.reduce((sum, cl) => {
    return sum + (cl.total_volume || 0);
  }, 0);

  const formatNumber = (num) => {
    if (!num) return '-';
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Link2 className="h-4 w-4 text-gray-500" />
            Market Forecast Basis
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenLinkDialog}
            className="h-8"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Link Forecast
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            Loading...
          </div>
        ) : linkedCarlines.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Link2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No market forecast linked</p>
            <p className="text-xs text-gray-400 mt-1">
              {programId
                ? 'Link a forecast for this Program-Part context'
                : 'Master-part links are legacy/global; select a Program 360 context for S&OP'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary */}
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs text-blue-600 font-medium">
                    {programId ? 'Program-Part Market Volume' : 'Master-Part Base Volume'}
                  </div>
                  <div className="text-xl font-bold text-blue-900">{formatNumber(totalBaseVolume)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">{linkedCarlines.length} carline(s)</div>
                </div>
              </div>
              {/* Sync to Grid Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualSync}
                disabled={syncing}
                className="w-full h-8 text-xs bg-white"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <ArrowDownToLine className="h-3.5 w-3.5 mr-1" />
                    {programId ? 'Use in this S&OP Context' : 'Sync Volume to S&OP Grid'}
                  </>
                )}
              </Button>
              
              {/* Sync Result Message */}
              {syncResult && (
                <div className={`mt-2 p-2 rounded text-xs ${
                  syncResult.success 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {syncResult.success ? (
                    <div className="flex items-start gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Sync Complete</div>
                        {syncResult.details && (
                          <div className="mt-1 text-green-600">
                            {syncResult.details.scenario_created && <div>• Created baseline scenario</div>}
                            {syncResult.details.records_created > 0 && (
                              <div>• Created {syncResult.details.records_created} forecast records</div>
                            )}
                            {syncResult.details.records_updated > 0 && (
                              <div>• Updated {syncResult.details.records_updated} existing records</div>
                            )}
                            {syncResult.details.years?.length > 0 && (
                              <div>• Years: {Math.min(...syncResult.details.years)}-{Math.max(...syncResult.details.years)}</div>
                            )}
                            {syncResult.details.dates_auto_filled && syncResult.details.dates_info?.length > 0 && (
                              <div className="mt-1 text-blue-600">• Dates auto-filled: {syncResult.details.dates_info.join(', ')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <div>{syncResult.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Linked Carlines List */}
            <div className="space-y-2">
              {linkedCarlines.map((link) => (
                <div 
                  key={link.id} 
                  className="p-3 bg-gray-50 rounded-lg group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {link.program_name || link.carline_name || `Carline ${link.core_mnemonic}`}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                        <span>{link.oem}</span>
                        {link.platform && <span className="text-gray-400">•</span>}
                        {link.platform && <span>{link.platform}</span>}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleUnlinkCarline(link.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* IHS Data Details */}
                  <div className="mt-2 pt-2 border-t border-gray-200 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">SOP:</span>
                      <span className="font-medium">{link.sop ? new Date(link.sop).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">EOP:</span>
                      <span className="font-medium">{link.eop ? new Date(link.eop).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Peak Year:</span>
                      <span className="font-medium">{link.peak_year || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Peak Vol:</span>
                      <span className="font-medium">{link.peak_volume ? formatNumber(link.peak_volume) : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-500">Total Lifecycle Vol:</span>
                      <span className="font-medium text-blue-600">{formatNumber(link.total_volume)} units</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Link Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link Market Forecast</DialogTitle>
              <DialogDescription>
                {programId
                  ? `Apply an IHS/S&P Mobility forecast to ${programContext?.program_name || 'this Program-Part context'}.`
                  : 'Search and select an IHS/S&P Mobility carline for the legacy master-part link.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <Input
                placeholder="Search by program name, OEM..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                {availableCarlines.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {searchTerm ? 'No carlines found' : 'Type to search carlines'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {availableCarlines.map((carline) => {
                      const isLinked = linkedCarlines.some(
                        lc => lc.core_mnemonic === carline.core_mnemonic
                      );
                      
                      return (
                        <button
                          key={carline.core_mnemonic}
                          className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                            isLinked ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => !isLinked && handleLinkCarline(carline)}
                          disabled={isLinked || linking}
                        >
                          <div className="font-medium text-sm">
                            {carline.program_name || carline.name_plate}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span>{carline.oem}</span>
                            <span>{carline.region}</span>
                            {carline.sop_year && <span>SOP {carline.sop_year}</span>}
                          </div>
                          {isLinked && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              Already linked
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
